import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 px-8 lg:px-16 py-4" data-testid="navigation">
        <div className="max-w-7xl mx-auto">
          <div className="glass-card px-6 py-3 rounded-sm flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img 
                src="/solara-logo.png" 
                alt="Solara Global Capital" 
                className="h-8 w-8 object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? "text-[#bd9a59]"
                      : "text-slate-300 hover:text-white"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href="https://app.solaraglobal.au/apply"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors flex items-center gap-1"
                data-testid="nav-loan-application"
              >
                Loan Application
                <ExternalLink className="w-3 h-3" />
              </a>
              <Link to="/booking">
                <button
                  className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] px-5 py-2 text-xs font-semibold tracking-wide uppercase transition-all rounded-sm"
                  data-testid="nav-book-call-btn"
                >
                  Book a Call
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* New Website Banner - Below Nav */}
          <div className="mt-3">
            <a 
              href="https://www.solaraglobal.com.au" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block bg-[#bd9a59] text-slate-950 px-4 py-2.5 text-center rounded-sm hover:bg-[#d4b270] transition-colors"
              data-testid="new-website-banner"
            >
              <span className="text-sm font-semibold inline-flex items-center gap-2">
                Visit our new website
                <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="md:hidden mt-2"
              >
                <div className="glass-card p-4 rounded-sm flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-sm font-medium py-2 transition-colors ${
                        isActive(link.path)
                          ? "text-[#bd9a59]"
                          : "text-slate-300 hover:text-white"
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <a
                    href="https://app.solaraglobal.au/apply"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium py-2 text-slate-300 hover:text-white transition-colors flex items-center gap-1"
                  >
                    Loan Application
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
                    <button className="w-full bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] px-5 py-3 text-xs font-semibold tracking-wide uppercase transition-all rounded-sm">
                      Book a Call
                    </button>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
